import { cn } from "@/core/utils/custom-styles";
import type { Table } from "@tanstack/react-table";
import { InputSelect, Pagination } from "@woba/ds-web-next";
import { useId } from "react";
import { useTranslation } from "react-i18next";

/** Tamanho de página padrão das tabelas no padrão Workbase (Figma: "15"). */
export const WORKBASE_DEFAULT_PAGE_SIZE = 15;
export const DEFAULT_PAGE_SIZE_OPTIONS = [WORKBASE_DEFAULT_PAGE_SIZE, 25, 50];

interface DataTableDsPaginationProps<TData> {
  table: Table<TData>;
  /** Opções do seletor "Linhas por página"; vazio esconde o seletor. */
  pageSizeOptions?: number[];
  disabled?: boolean;
  className?: string;
}

/**
 * Rodapé da tabela no padrão do Workbase DS (Figma 6043:2726, "Bottom
 * Actions"): seletor de linhas por página à esquerda e `Pagination` do DS
 * (círculos, página ativa preenchida, reticências e última página) à direita.
 * Trocar o tamanho da página volta para a página 1, para não cair numa página
 * que deixou de existir.
 */
export function DataTableDsPagination<TData>({
  table,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  disabled = false,
  className,
}: DataTableDsPaginationProps<TData>) {
  const [translate] = useTranslation(["core"]);
  const selectId = useId();
  // Mocks de tabela sem estado de paginação caem no padrão (página 1).
  const { pageIndex, pageSize } = table.getState().pagination ?? {
    pageIndex: 0,
    pageSize: WORKBASE_DEFAULT_PAGE_SIZE,
  };
  const pageCount = Math.max(1, table.getPageCount());
  const options = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  const handlePageSizeChange = (value: string | string[] | null) => {
    const next = Number(Array.isArray(value) ? value[0] : value);
    if (!Number.isFinite(next) || next <= 0 || next === pageSize) return;
    table.setPagination({ pageIndex: 0, pageSize: next });
  };

  return (
    <div
      data-testid="data-table-ds-pagination"
      className={cn(
        "flex flex-col gap-4 md:flex-row md:items-center md:justify-between",
        className,
      )}
    >
      {pageSizeOptions.length > 0 ? (
        <div className="flex items-center gap-4">
          <InputSelect
            id={selectId}
            className="shrink-0"
            style={{ width: 112 }}
            disabled={disabled}
            value={String(pageSize)}
            options={options.map((option) => ({
              value: String(option),
              label: String(option),
            }))}
            onChange={handlePageSizeChange}
          />
          <label
            htmlFor={selectId}
            className="text-[length:var(--woba-typography-caption-medium-font-size)] font-medium text-[color:var(--woba-color-content-subtle)]"
          >
            {translate("data-table-rows-per-page")}
          </label>
        </div>
      ) : (
        <span />
      )}

      <Pagination
        className="self-center md:self-auto"
        count={pageCount}
        page={pageIndex + 1}
        disabled={disabled}
        accessibilityLabel={translate("data-table-pagination-label") as string}
        onPageChange={(page) => table.setPageIndex(page - 1)}
      />
    </div>
  );
}
